/**
 * Toast Notification Utilities
 * Wrapper around react-hot-toast for consistent usage
 */

import hotToast from 'react-hot-toast';

/**
 * Show a success toast
 * @param {string} message - Message to display
 * @param {Object} options - Additional toast options
 */
export const showSuccess = (message, options = {}) => {
  hotToast.success(message, options);
};

/**
 * Show an error toast
 * @param {string} message - Message to display
 * @param {Object} options - Additional toast options
 */
export const showError = (message, options = {}) => {
  hotToast.error(message, options);
};

/**
 * Show a loading toast that can be updated
 * @param {string} message - Message to display
 * @returns {string} - Toast ID for updating/dismissing
 */
export const showLoading = (message = 'Loading...') => {
  return hotToast.loading(message);
};

/**
 * Dismiss a specific toast
 * @param {string} toastId - ID of toast to dismiss
 */
export const dismissToast = (toastId) => {
  hotToast.dismiss(toastId);
};

/**
 * Show a promise toast (loading → success/error)
 * @param {Promise} promise - Promise to track
 * @param {Object} messages - Messages for each state
 */
export const showPromise = (promise, messages = {}) => {
  return hotToast.promise(promise, {
    loading: messages.loading || 'Loading...',
    success: messages.success || 'Success!',
    error: messages.error || 'Something went wrong'
  });
};

/**
 * Show an info toast
 * @param {string} message - Message to display
 */
export const showInfo = (message) => {
  hotToast(message, {
    icon: 'ℹ️',
    style: {
      background: '#3B82F6',
      color: '#fff'
    }
  });
};

/**
 * Show a warning toast
 * @param {string} message - Message to display
 */
export const showWarning = (message) => {
  hotToast(message, {
    icon: '⚠️',
    style: {
      background: '#F59E0B',
      color: '#fff'
    },
    duration: 4000
  });
};

/**
 * Toast utility object
 * Usage: toast.success('message'), toast.error('message'), etc.
 */
export const toast = {
  success: showSuccess,
  error: showError,
  loading: showLoading,
  dismiss: dismissToast,
  promise: showPromise,
  info: showInfo,
  warning: showWarning
};

export default toast;
